import React, { useContext, useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { FiDownload } from 'react-icons/fi'
import utility from 'src/utils/utility'
import {
    Box, Flex, Text, tokens,
    Button, SelectField, DateRangeFilterButton,
    InstitutionTable, TableColumn, TablePagination,
} from 'src/componentv2'
import { downloadCsv } from 'src/utils/csvExport'
import { TotalResponses } from 'src/assets/svg/total-survey-responses'
import { AverageSurvey } from 'src/assets/svg/average-survey'
import { ThumbsUp } from 'src/assets/svg/thumbs-up'
import { ThumbsDown } from 'src/assets/svg/thumbs-down'
import ConfigurationApiContext, { InstitutionListItem } from 'src/provider/API/v2/call-service/configuration'
import SurveyApiContext, { SurveySummary, SurveyTrigger } from 'src/provider/API/v2/call-service/Survey'

const PAGE_PADDING_X = '3.2rem'
const PAGE_SIZE = 10

interface ParsedResponseRow {
    sn: number
    surveyTriggerID: number
    profileID: number
    responses: { question?: string; answer?: string }[]
}

function escapeCsvValue(value: unknown): string {
    if (value === null || value === undefined) return ''
    const str = String(value)
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

const SurveyReportPage = () => {
    const { getInstitutions } = useContext(ConfigurationApiContext)
    const { getSurveyTriggers, getInstitutionSurveySummary, surveyResponse } = useContext(SurveyApiContext)

    const [institutions, setInstitutions] = useState<InstitutionListItem[]>([])
    const [selectedInstitutionCode, setSelectedInstitutionCode] = useState('')
    const [triggers, setTriggers] = useState<SurveyTrigger[]>([])
    const [selectedTriggerId, setSelectedTriggerId] = useState('')
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)

    const [summary, setSummary] = useState<SurveySummary | null>(null)
    const [rows, setRows] = useState<ParsedResponseRow[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        (async () => {
            const { items } = await getInstitutions({ FetchAll: true })
            setInstitutions(items ?? [])
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const currentFilter = () => ({
        surveyTriggerID: selectedTriggerId || undefined,
        startDateTime: dateRange?.from ? dateRange.from.toISOString() : undefined,
        endDateTime: dateRange?.to ? dateRange.to.toISOString() : undefined,
    })

    const fetchTriggers = async () => {
        if (!selectedInstitutionCode) return
        const response = await getSurveyTriggers(selectedInstitutionCode)
        setTriggers(response || [])
    }

    const fetchSummary = async () => {
        if (!selectedInstitutionCode) return
        const response = await getInstitutionSurveySummary(selectedInstitutionCode, currentFilter())
        setSummary(response || null)
    }

    const fetchResponses = async (pageNumber = 1) => {
        if (!selectedInstitutionCode) return
        setLoading(true)
        const response = await surveyResponse(pageNumber, PAGE_SIZE, selectedInstitutionCode, currentFilter())
        if (response) {
            const parsed: ParsedResponseRow[] = (response.data || []).map((item: any, index: number) => ({
                sn: (pageNumber - 1) * PAGE_SIZE + index + 1,
                surveyTriggerID: item.surveyTriggerID,
                profileID: item.profileID,
                responses: item.responses ? JSON.parse(item.responses) : [],
            }))
            setRows(parsed)
            setTotal(response.total || 0)
        } else {
            setRows([])
            setTotal(0)
        }
        setLoading(false)
    }

    useEffect(() => {
        setPage(1)
        fetchTriggers()
        fetchSummary()
        fetchResponses(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionCode, selectedTriggerId, dateRange])

    useEffect(() => {
        fetchResponses(page)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    const handleDownload = async () => {
        if (!selectedInstitutionCode) return
        const response = await surveyResponse(1, PAGE_SIZE, selectedInstitutionCode, { ...currentFilter(), returnMax: true })
        if (!response?.data?.length) return

        const headerRow = ['S/N', 'Survey Trigger ID', 'Profile ID', 'Responses'].join(',')
        const csvRows = response.data.map((item: any, index: number) => {
            const parsedResponses = item.responses ? JSON.parse(item.responses) : []
            const responseText = parsedResponses
                .map((r: any) => `${r.Question || r.question}: ${r.Answer || r.answer}`)
                .join(' | ')
            return [index + 1, item.surveyTriggerID, item.profileID, responseText].map(escapeCsvValue).join(',')
        })

        downloadCsv(`survey-report-${new Date().toISOString().slice(0, 10)}.csv`, [headerRow, ...csvRows].join('\n'))
    }

    const summaryData = [
        { icon: <TotalResponses />, label: 'Total Responses', value: summary?.totalResponses || 0 },
        { icon: <AverageSurvey />, label: 'Average Rating', value: summary?.averageRating || 0 },
        { icon: <ThumbsUp />, label: 'Positive Responses', value: summary?.totalPositiveResponses || 0 },
        { icon: <ThumbsDown />, label: 'Negative Responses', value: summary?.totalNegativeResponses || 0 },
    ]

    const columns: TableColumn<ParsedResponseRow>[] = [
        { key: 'sn', title: 'S/N', width: '6rem', dataIndex: 'sn' },
        {
            key: 'surveyType',
            title: 'Survey Type',
            render: (row) => {
                const trigger = triggers.find((t) => t.id === row.surveyTriggerID)
                return <Text fontSize="1.4" color={tokens.textMuted}>{trigger ? trigger.triggerName : 'Not Available'}</Text>
            },
        },
        { key: 'profileID', title: 'Profile ID', dataIndex: 'profileID' },
        {
            key: 'responses',
            title: 'Responses',
            render: (row) => (
                <Box>
                    {row.responses.map((r, i) => (
                        <Text key={i} fontSize="1.3" color={tokens.textMuted} style={{ display: 'block' }}>
                            {r.question ?? (r as any).Question} : <b>{r.answer ?? (r as any).Answer}</b>
                        </Text>
                    ))}
                </Box>
            ),
        },
    ]

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Flex justify="space-between" items="center" wrap="wrap" gap="1.2">
                <Box>
                    <Text fontSize="2.4" fontWeight="700" color={tokens.textDark}>Survey Report</Text>
                    <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>
                        Review customer survey responses per institution.
                    </Text>
                </Box>
                <Button variant="outline" disabled={!selectedInstitutionCode} onClick={handleDownload}>
                    <FiDownload size={14} style={{ marginRight: '0.6rem' }} />
                    Download
                </Button>
            </Flex>

            <Flex gap="1.6" wrap="wrap" topMargin="2.4">
                <Box style={{ maxWidth: '28rem', width: '100%' }}>
                    <SelectField
                        label="Institution"
                        placeholder="Select an institution"
                        value={selectedInstitutionCode}
                        onChange={setSelectedInstitutionCode}
                        options={institutions.map((item) => ({ label: item.name, value: String(item.institutionCode) }))}
                    />
                </Box>
                <Box style={{ maxWidth: '24rem', width: '100%' }}>
                    <SelectField
                        label="Survey Trigger"
                        placeholder="All triggers"
                        value={selectedTriggerId}
                        onChange={setSelectedTriggerId}
                        options={triggers.map((t) => ({ label: t.triggerName, value: String(t.id) }))}
                        disabled={!selectedInstitutionCode}
                    />
                </Box>
                <Box>
                    <Text fontSize="1.4" fontWeight="500" color={tokens.textDark} style={{ marginBottom: '0.6rem', display: 'block' }}>Date Range</Text>
                    <DateRangeFilterButton value={dateRange} onChange={setDateRange} />
                </Box>
            </Flex>

            {!selectedInstitutionCode ? (
                <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard}>
                    <Flex justify="center" style={{ padding: '4.8rem 0' }}>
                        <Text fontSize="1.4" color={tokens.textMuted}>Select an institution to view its survey report.</Text>
                    </Flex>
                </Box>
            ) : (
                <>
                    <Flex gap="1.6" wrap="wrap" topMargin="2.4">
                        {summaryData.map((item) => (
                            <Box
                                key={item.label}
                                bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard}
                                padding="1.6" style={{ flex: '1 1 20rem' }}
                            >
                                <Flex items="center" gap="1.2">
                                    {item.icon}
                                    <Box>
                                        <Text fontSize="1.3" color={tokens.textMuted}>{item.label}</Text>
                                        <Text fontSize="2" fontWeight="700" color={tokens.textDark}>{item.value}</Text>
                                    </Box>
                                </Flex>
                            </Box>
                        ))}
                    </Flex>

                    <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                        <InstitutionTable columns={columns} data={rows} loading={loading} emptyText="No survey responses found." />

                        <Box style={{ padding: '1.6rem', borderTop: `1px solid ${tokens.border}` }}>
                            <TablePagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
                        </Box>
                    </Box>
                </>
            )}
        </div>
    )
}

export default utility.routeData('survey-report', 'SurveyReport', <SurveyReportPage />)
